import { useCallback, useEffect, useMemo, useState } from "react";

import { useAppDispatch } from "@/redux/hooks";
import groupByField from "@/utils/groupByField";
import {
  api,
  type GetUnconfirmedPaymentsApiArg,
} from "@generated/redux/services/generated/api";
import {
  FilteredBlockchainWithdrawalForList,
  FilteredPaymentForList,
} from "@generated/types";

export type PaymentListItem =
  | (FilteredPaymentForList & { _type: "payment" })
  | (FilteredBlockchainWithdrawalForList & { _type: "withdrawal" });

type PaymentListItemWithDateKey = PaymentListItem & { dateKey: string };

interface PaymentsQueryData {
  payments?: FilteredPaymentForList[];
  withdrawals?: FilteredBlockchainWithdrawalForList[];
}

interface UseGroupedPaymentsListOptions {
  queryData: PaymentsQueryData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  /** When true, clears selected item if it disappears from the list (e.g., after confirmation) */
  clearOnRemoval?: boolean;
  /** Query args used for the RTK Query cache entry — required for optimistic removeItem */
  queryArgs?: GetUnconfirmedPaymentsApiArg;
}

export default function useGroupedPaymentsList({
  queryData,
  isLoading,
  isFetching,
  clearOnRemoval = false,
  queryArgs,
}: UseGroupedPaymentsListOptions) {
  const dispatch = useAppDispatch();
  // Combine payments and withdrawals into a single array
  const combinedItems = useMemo(() => {
    const payments = queryData?.payments || [];
    const withdrawals = queryData?.withdrawals || [];

    const combined: PaymentListItem[] = [
      ...payments.map(
        (p) => ({ ...p, _type: "payment" as const }) as PaymentListItem
      ),
      ...withdrawals.map(
        (w) => ({ ...w, _type: "withdrawal" as const }) as PaymentListItem
      ),
    ];

    return combined;
  }, [queryData]);

  const isEmpty = !isLoading && !isFetching && combinedItems.length === 0;
  const isInitialLoad = isLoading && !queryData;
  const isLoadingOrFetching = isLoading || isFetching || isInitialLoad;

  const [selectedItem, setSelectedItem] = useState<PaymentListItem | null>(
    null
  );
  const [selectedIndex, setSelectedIndex] = useState<string | null>(null);

  // Sync selectedItem with updated list data when list is refetched
  useEffect(() => {
    if (selectedItem) {
      const updatedItem = combinedItems.find(
        (item) => item.id === selectedItem.id
      );
      if (updatedItem) {
        setSelectedItem(updatedItem);
      } else if (clearOnRemoval) {
        // Item was removed from list (e.g., confirmed and moved to processing)
        setSelectedItem(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combinedItems]);

  // Add dateKey property for grouping by payment/withdrawal date
  const itemsWithDateKey = useMemo(() => {
    return combinedItems.map((item) => {
      const fullDate =
        item._type === "payment" ? item.paymentDate : item.withdrawalDate || "";
      const dateOnly = (fullDate ?? "").split("T")[0] || "";
      return {
        ...item,
        dateKey: dateOnly,
      };
    }) as PaymentListItemWithDateKey[];
  }, [combinedItems]);

  // Group items by date
  const groupedItems = useMemo(() => {
    return groupByField<PaymentListItemWithDateKey>(
      itemsWithDateKey,
      "dateKey",
      {
        sortItems: (a, b) => {
          const dateA = a.meta?.createTime || "";
          const dateB = b.meta?.createTime || "";

          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;

          return new Date(dateB).getTime() - new Date(dateA).getTime();
        },
      }
    );
  }, [itemsWithDateKey]);

  const handleOnClose = () => {
    setSelectedItem(null);
    setSelectedIndex(null);
  };

  const selectItem = (item: PaymentListItem, index: number) => {
    setSelectedIndex(`${item.id}-${index}`);
    setSelectedItem(item);
  };

  const removeItem = useCallback(
    (itemId: string) => {
      if (!queryArgs) return;
      dispatch(
        api.util.updateQueryData(
          "getUnconfirmedPayments",
          queryArgs,
          (draft) => {
            if (draft.payments) {
              draft.payments = draft.payments.filter((p) => p.id !== itemId);
            }
            if (draft.withdrawals) {
              draft.withdrawals = draft.withdrawals.filter(
                (w) => w.id !== itemId
              );
            }
          }
        )
      );
      setSelectedItem(null);
      setSelectedIndex(null);
    },
    [dispatch, queryArgs]
  );

  return {
    combinedItems,
    isEmpty,
    isInitialLoad,
    isLoadingOrFetching,
    selectedItem,
    selectedIndex,
    groupedItems,
    handleOnClose,
    selectItem,
    removeItem,
  };
}
